import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  MouseEvent,
} from "react";
import {
  Modal,
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Chip,
  Slider,
  LinearProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  WalkthroughStep,
  CaptionPosition,
  GroupInfo,
  Language,
  Localized,
  resolveLocalized,
  WALKTHROUGH_LANGUAGE_STORAGE_KEY,
} from "./useWalkthrough";
import {
  Close,
  ArrowBackIosNew,
  ArrowForwardIos,
  Translate,
  VolumeUp,
  Pause,
  PlayArrow,
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";

export type WalkthroughVariant = "classic" | "paged";

// Render a step text field. Plain strings get a lightweight **bold** parser so
// authors can write "Click the **NEW** button" without touching JSX.
const renderRichText = (content: React.ReactNode): React.ReactNode => {
  if (typeof content !== "string") return content;
  if (!content.includes("**")) return content;
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <Box
          key={i}
          component="strong"
          sx={{ color: "primary.main", fontWeight: 600 }}
        >
          {part.slice(2, -2)}
        </Box>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

// Flatten a ReactNode to plain text for the speech synthesizer. Strips the
// **bold** markers used by renderRichText and walks element children so JSX
// step content still gets read aloud.
const nodeToText = (node: React.ReactNode): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node.replace(/\*\*([^*]+)\*\*/g, "$1");
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join(" ");
  if (React.isValidElement(node)) {
    const children = (node.props as { children?: React.ReactNode })?.children;
    return nodeToText(children);
  }
  return "";
};

export interface WalkthroughProps {
  currentStep: WalkthroughStep | null;
  onNext: () => void;
  onEnd: () => void;
  onScrollView?: () => void;
  isLastStep: boolean;
  // Paged variant additions — only consumed when variant === "paged".
  variant?: WalkthroughVariant;
  onPrevious?: () => void;
  currentStepIndex?: number;
  totalSteps?: number;
  groupInfo?: GroupInfo | null;
  // Label for the last-step CTA. Defaults to "Finish". Pass "Take Quiz" (or
  // similar) when the consumer wires a follow-up flow after the walkthrough.
  finishLabel?: string;
  // Optional handler invoked by the last-step CTA. When omitted, the CTA
  // falls back to onEnd so existing classic-variant consumers keep working.
  // Distinct from onEnd so the "Don't show again" dismiss path (also onEnd)
  // can skip any follow-up flow like a post-walkthrough quiz.
  onFinish?: () => void;
  // Optional "soft dismiss" — when provided, the close-confirm dialog gains a
  // 3-button layout: Continue / Skip for now (calls onDismiss) / Don't show
  // again (calls onEnd). When omitted, the existing 2-button confirm is used
  // and Close always routes to onEnd (current behavior for classic modules).
  onDismiss?: () => void;
  // Jump directly to any step index. Wired to the progress slider.
  onGoToStep?: (index: number) => void;
  // Ref set to `true` while a backward navigation is in progress. Suppresses
  // the "element not found → auto-advance" guard so a closing modal/menu
  // during the transition doesn't race-advance the tour forward.
  isBackwardNavigating?: React.RefObject<boolean>;
}

/* ------------------------- Caption placement helpers ------------------------ */

type RectLike = { top: number; left: number; width: number; height: number };

const CAPTION_W = 284;
const CAPTION_H = 160;
const GAP = 12; // gap from the highlighted rect
const EDGE_PAD = 8; // padding from viewport edges
const HOLE_RADIUS = 12;

const makeRect = (
  top: number,
  left: number,
  w = CAPTION_W,
  h = CAPTION_H,
): RectLike => ({
  top,
  left,
  width: w,
  height: h,
});

const rectsOverlap = (a: RectLike, b: RectLike) =>
  !(
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );

const clampToViewport = (r: RectLike) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let { top, left, width, height } = r;

  if (left < EDGE_PAD) left = EDGE_PAD;
  if (top < EDGE_PAD) top = EDGE_PAD;
  if (left + width > vw - EDGE_PAD) left = vw - EDGE_PAD - width;
  if (top + height > vh - EDGE_PAD) top = vh - EDGE_PAD - height;

  return { top, left, width, height };
};

const inViewport = (r: RectLike) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return (
    r.left >= EDGE_PAD &&
    r.top >= EDGE_PAD &&
    r.left + r.width <= vw - EDGE_PAD &&
    r.top + r.height <= vh - EDGE_PAD
  );
};

// `w`/`h` are the popover's actual measured dimensions (falling back to the
// CAPTION_* constants before the first measurement). Using the real height is
// what keeps tall paged cards from being placed where their footer would fall
// off-screen.
const getCandidateRects = (anchor: DOMRect, w = CAPTION_W, h = CAPTION_H) => [
  { key: "bottom", rect: makeRect(anchor.bottom + GAP, anchor.left, w, h) },
  { key: "right", rect: makeRect(anchor.top, anchor.right + GAP, w, h) },
  { key: "left", rect: makeRect(anchor.top, anchor.left - w - GAP, w, h) },
  { key: "top", rect: makeRect(anchor.top - h - GAP, anchor.left, w, h) },
  // corners
  {
    key: "bottom-right",
    rect: makeRect(anchor.bottom + GAP, anchor.right + GAP, w, h),
  },
  {
    key: "bottom-left",
    rect: makeRect(anchor.bottom + GAP, anchor.left - w - GAP, w, h),
  },
  {
    key: "top-right",
    rect: makeRect(anchor.top - h - GAP, anchor.right + GAP, w, h),
  },
  {
    key: "top-left",
    rect: makeRect(anchor.top - h - GAP, anchor.left - w - GAP, w, h),
  },
  {
    key: "center-left",
    rect: makeRect(anchor.top, anchor.left - 610 - GAP, w, h),
  },
  {
    key: "near-left",
    rect: makeRect(anchor.top, anchor.left - 520 - GAP, w, h),
  },
];

const orderByPreference = (preferred?: CaptionPosition): string[] => {
  const all: string[] = [
    "right", // Higher priority for horizontal placement
    "left", // Higher priority for horizontal placement
    "bottom", // Lower priority for vertical placement
    "top", // Lower priority for vertical placement
    "bottom-right",
    "bottom-left",
    "top-right",
    "top-left",
    "center-left",
    "near-left",
  ];
  return preferred ? [preferred, ...all.filter((k) => k !== preferred)] : all;
};

// Returns a style object that avoids overlapping any highlight and stays in viewport
const getCaptionStyle = (
  highlightRects: DOMRect[],
  preferred?: CaptionPosition,
  dims?: { w: number; h: number },
) => {
  const anchor = highlightRects[0];

  // Check if there's enough space for horizontal placement first
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Effective popover footprint used for all placement/clamping math. Width is
  // capped at CAPTION_W; height is the *measured* height clamped to the viewport
  // (the Paper itself is capped to the same height and scrolls internally), so
  // we never position a card whose footer would land off-screen.
  const w = Math.min(dims?.w || CAPTION_W, CAPTION_W);
  const h = Math.min(dims?.h || CAPTION_H, vh - EDGE_PAD * 2);

  const candidates = getCandidateRects(anchor, w, h);

  /* ---------------------------------
   * 1️⃣ SIDEBAR PRIORITY (LEFT EDGE)
   * --------------------------------- */
  const isNearLeftEdge = (rect: DOMRect) => rect.left < 120;

  if (isNearLeftEdge(anchor)) {
    const top = Math.min(vh - h - EDGE_PAD, Math.max(EDGE_PAD, anchor.top));

    const left = anchor.right + GAP;

    if (left + w <= vw - EDGE_PAD) {
      return {
        top,
        left,
        maxWidth: CAPTION_W,
      };
    }
  }

  /* ---------------------------------
   * 2️⃣ SMALL VIEWPORT FALLBACK
   * --------------------------------- */
  const isSmallViewport = vw < 768 || vh < 500;

  if (isSmallViewport) {
    // Center horizontally and pin near the vertical middle, clamped so the
    // (capped, internally-scrolling) card always sits fully inside the viewport.
    const top = Math.max(EDGE_PAD, Math.round((vh - h) / 2));
    return {
      top,
      left: "50%",
      transform: "translateX(-50%)",
      maxWidth: Math.min(vw - 32, CAPTION_W),
    };
  }

  /* ---------------------------------
   * 3️⃣ LARGE ELEMENT FALLBACK
   * --------------------------------- */
  // Fires when the anchor is a large portal-rendered dialog that covers most of
  // the viewport. MUI Dialog Papers are centered in the VIEWPORT (not the content
  // area), so their left edge is (vw - dialogWidth) / 2. A nearly-full-width
  // dialog (e.g. 1300 px on 1470 px) starts at left ≈ 85 px; a moderately wide
  // one (1200 px) starts at left ≈ 135 px. The sidebar content area begins at
  // ≈ 230 px, so using anchor.left < 200 catches all these dialogs while
  // excluding sidebar-offset tables and panels.
  const fillsViewport =
    anchor.left < 200 &&
    anchor.width > vw * 0.75 &&
    anchor.height > vh * 0.5;
  if (fillsViewport) {
    // Pin inside the dialog at its own top-left corner so the tooltip sits in
    // the dialog title-bar area rather than behind the sidebar navigation.
    return {
      top: anchor.top + EDGE_PAD,
      left: anchor.left + EDGE_PAD,
      maxWidth: CAPTION_W,
    };
  }

  /* ---------------------------------
   * 4️⃣ NORMAL CANDIDATE PLACEMENT
   * --------------------------------- */
  const hasSpaceRight = anchor.right + GAP + w <= vw - EDGE_PAD;
  const hasSpaceLeft = anchor.left - GAP - w >= EDGE_PAD;
  const hasSpaceBottom = anchor.bottom + GAP + h <= vh - EDGE_PAD;
  const hasSpaceTop = anchor.top - GAP - h >= EDGE_PAD;

  // Prioritize horizontal placement if space is available
  const prioritizedOrder = preferred
    ? [preferred, ...orderByPreference().filter((k) => k !== preferred)]
    : [
      ...(hasSpaceRight ? ["right"] : []),
      ...(hasSpaceLeft ? ["left"] : []),
      ...(hasSpaceBottom ? ["bottom"] : []),
      ...(hasSpaceTop ? ["top"] : []),
      "bottom-right",
      "bottom-left",
      "top-right",
      "top-left",
      "center-left",
      "near-left",
    ];

  // Inflate highlights slightly so we don't "kiss" their edges
  const inflated: RectLike[] = highlightRects.map((r) => ({
    top: r.top - 6,
    left: r.left - 6,
    width: r.width + 12,
    height: r.height + 12,
  }));

  // "center-left" / "near-left" are far-left slots designed to sit over the
  // sidebar. With a wide highlight (e.g. a full form container) the slot
  // clamps to the viewport edge and technically overlaps the highlight's
  // left edge — that's intentional, so honor the author's choice instead of
  // falling through to a worse placement.
  const isFarLeftSlot = preferred === "center-left" || preferred === "near-left";

  for (const key of prioritizedOrder) {
    const found = candidates.find((c) => c.key === key);
    if (!found) continue;

    let candidate = found.rect;
    if (!inViewport(candidate)) candidate = clampToViewport(candidate);

    const overlaps = inflated.some((hl) => rectsOverlap(candidate, hl));
    const skipOverlapCheck = isFarLeftSlot && key === preferred;
    if ((skipOverlapCheck || !overlaps) && inViewport(candidate)) {
      return { top: candidate.top, left: candidate.left, maxWidth: CAPTION_W };
    }
  }

  // Fallback: clamp bottom placement and nudge horizontally if overlapping
  let fallback = clampToViewport(makeRect(anchor.bottom + GAP, anchor.left, w, h));
  let attempts = 0;
  let dir = 1;
  while (inflated.some((hl) => rectsOverlap(fallback, hl)) && attempts < 20) {
    const next = makeRect(
      fallback.top - 10,
      fallback.left + dir * 24,
      fallback.width,
      fallback.height,
    );
    fallback = clampToViewport(next);
    dir *= -1;
    attempts++;
  }
  return { top: fallback.top, left: fallback.left, maxWidth: CAPTION_W };
};

/* ------------------------ Multihole overlay (clip-path) --------------------- */

// Build a rounded-rectangle path using SVG arc commands
const roundedRectPath = (
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  const x2 = x + w;
  const y2 = y + h;
  return [
    `M ${x + rr} ${y}`,
    `H ${x2 - rr}`,
    `A ${rr} ${rr} 0 0 1 ${x2} ${y + rr}`,
    `V ${y2 - rr}`,
    `A ${rr} ${rr} 0 0 1 ${x2 - rr} ${y2}`,
    `H ${x + rr}`,
    `A ${rr} ${rr} 0 0 1 ${x} ${y2 - rr}`,
    `V ${y + rr}`,
    `A ${rr} ${rr} 0 0 1 ${x + rr} ${y}`,
    "Z",
  ].join(" ");
};

// Use CSS path() with evenodd fill-rule so inner subpaths become true holes
const createClipPath = (
  rects: DOMRect[],
  padding = 4,
  radius = HOLE_RADIUS,
) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Outer full-screen rectangle
  const outer = `M 0 0 H ${vw} V ${vh} H 0 Z`;

  // One rounded-rect subpath per hole
  const holes = rects
    .map((r) => {
      const x = Math.max(0, r.left - padding);
      const y = Math.max(0, r.top - padding);
      const w = Math.min(vw - x, r.width + padding * 2);
      const h = Math.min(vh - y, r.height + padding * 2);
      return roundedRectPath(x, y, w, h, radius);
    })
    .join(" ");

  const d = `${outer} ${holes}`;
  return `path(evenodd, '${d}')`;
};

/* -------------------------------- Component -------------------------------- */

const Walkthrough: React.FC<WalkthroughProps> = ({
  currentStep,
  onNext,
  onEnd,
  isLastStep,
  onScrollView,
  variant = "classic",
  onPrevious,
  currentStepIndex,
  totalSteps,
  groupInfo,
  onDismiss,
  onGoToStep,
  finishLabel = "Finish",
  onFinish,
  isBackwardNavigating,
}) => {
  const isPaged = variant === "paged";
  const [highlightRects, setHighlightRects] = useState<DOMRect[]>([]);

  // Slider draft — tracks thumb position while dragging without jumping the
  // walkthrough. Syncs back to currentStepIndex on Next/Prev/external changes.
  const [sliderDraft, setSliderDraft] = useState<number>(currentStepIndex ?? 0);
  useEffect(() => {
    setSliderDraft(currentStepIndex ?? 0);
  }, [currentStepIndex]);

  // Actual rendered size of the popover card. Placement math uses this instead
  // of the fixed CAPTION_* constants so a tall card (chips + caption + sub-
  // captions + footer) is never positioned where its navigation buttons would
  // fall off-screen on shorter tablet/laptop viewports.
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [paperSize, setPaperSize] = useState<{ w: number; h: number } | null>(
    null,
  );

  const [openDialog, setOpenDialog] = useState(false);

  const [numberOfClicks, setNumberOfClicks] = useState(0);

  // Active language for any Localized<…> text in steps. Persisted across
  // sessions so users don't re-toggle on every walkthrough launch.
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem(WALKTHROUGH_LANGUAGE_STORAGE_KEY);
      if (saved === "en" || saved === "tl") return saved;
    } catch {
      /* ignore */
    }
    return "en";
  });
  const setLanguageAndPersist = (next: Language) => {
    setLanguage(next);
    try {
      localStorage.setItem(WALKTHROUGH_LANGUAGE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  // Resolve a Localized field to the active language, then run it through
  // the **bold** rich-text parser. Use everywhere a step text field is shown.
  const renderL = (value: Localized<React.ReactNode>) =>
    renderRichText(resolveLocalized(value, language));

  /* ------------------------------ Text-to-speech ----------------------------- */

  // Chrome's built-in TTS voices are robotic — hide the read-aloud button on Chrome.
  // Edge also includes "Chrome" in its UA, so exclude it via the absence of "Edg/".
  const isChrome =
    typeof navigator !== "undefined" &&
    /Chrome\//.test(navigator.userAgent) &&
    !/Edg\//.test(navigator.userAgent);

  // Three-state TTS: idle (!isSpeaking) → speaking (isSpeaking && !isPaused)
  // → paused (isSpeaking && isPaused). The speaker icon swaps accordingly.
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Persistent "auto-play intent": once the user starts read-aloud it stays
  // armed across steps, so narration keeps going on Next even after a step's
  // audio ended naturally. Cleared only when the user explicitly stops (pause)
  // — distinct from isSpeaking, which flips false the instant an utterance ends.
  const [autoPlay, setAutoPlay] = useState(false);
  // Tracks the utterance currently owning state. When we cancel() to start
  // a new one (e.g. on step change while continuing), the old utterance's
  // onend still fires asynchronously — we ignore it if it's no longer ours.
  const currentUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const highlightTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const resumeCharIdxRef = useRef(0);
  // Comma-separated "id:<id>" / "sel:<selector>" tokens for the field(s) whose
  // sentence is currently being spoken. Drives the blink useEffect below.
  const [activeHighlightKey, setActiveHighlightKey] = useState('');

  const localizedToText = (value: Localized<React.ReactNode>) =>
    nodeToText(resolveLocalized(value, language));

  // Returns every readable segment of the current step in visual order.
  // Each element is one "sentence" for field-highlight phrase matching.
  const buildStepParts = (): string[] => {
    if (!currentStep) return [];
    const parts: string[] = [];
    const title = localizedToText(currentStep.title);
    if (title) parts.push(title);
    const caption = localizedToText(currentStep.caption);
    if (caption) parts.push(caption);
    if (Array.isArray(currentStep.subCaption)) {
      currentStep.subCaption.forEach((section) => {
        const sectionTitle = localizedToText(section.title);
        if (sectionTitle) parts.push(sectionTitle);
        if (Array.isArray(section.subTitle)) {
          section.subTitle.forEach((sub) => {
            const s = localizedToText(sub.subTitle);
            if (s) parts.push(s);
            if (Array.isArray(sub.miniTitle)) {
              sub.miniTitle.forEach((mini) => {
                const m = localizedToText(mini.miniTitle);
                if (m) parts.push(m);
                if (Array.isArray(mini.superMiniTitle)) {
                  mini.superMiniTitle.forEach((sm) => {
                    const x = localizedToText(sm.superMiniTitle);
                    if (x) parts.push(x);
                  });
                }
              });
            }
          });
        }
      });
    }
    return parts;
  };

  // Concatenate every readable field on the current step in the same order it
  // appears visually. Joined with ". " so the synthesizer pauses between
  // sections instead of running everything together.
  const buildStepText = (): string => buildStepParts().join(". ");

  const pickVoice = (lang: Language): SpeechSynthesisVoice | undefined => {
    if (typeof window === "undefined" || !window.speechSynthesis) return undefined;
    const voices = window.speechSynthesis.getVoices();
    if (lang === "tl") {
      // 1. Google cloud Filipino voice — Chrome exposes "Google Filipino" (fil-PH)
      //    when online; sounds significantly more natural than offline system voices.
      const googleFil = voices.find(
        (v) =>
          v.name.toLowerCase().includes("google") &&
          (v.lang.toLowerCase().startsWith("fil") || v.lang.toLowerCase().startsWith("tl")),
      );
      if (googleFil) return googleFil;
      // 2. Any local Filipino / Tagalog voice as offline fallback
      return (
        voices.find((v) => v.lang.toLowerCase().startsWith("fil")) ||
        voices.find((v) => v.lang.toLowerCase().startsWith("tl"))
      );
    }
    const enVoices = voices.filter((v) => v.lang.toLowerCase().startsWith("en"));
    // 1. Google cloud English voices — "Google US English" is online/neural quality
    const googleUS = enVoices.find(
      (v) => v.name.toLowerCase().includes("google") && v.lang.toLowerCase() === "en-us",
    );
    if (googleUS) return googleUS;
    const anyGoogle = enVoices.find((v) => v.name.toLowerCase().includes("google"));
    if (anyGoogle) return anyGoogle;
    // 2. Neural/natural voices (Edge/Chrome expose Microsoft Online Natural voices)
    const natural = enVoices.find((v) => v.name.toLowerCase().includes("natural"));
    if (natural) return natural;
    // 3. Known clear Microsoft neural voices by name
    for (const name of ["Jenny", "Aria", "Zira", "Guy", "Libby", "Ryan"]) {
      const match = enVoices.find((v) => v.name.includes(name));
      if (match) return match;
    }
    // 4. Fall back to en-US, then any English voice
    return enVoices.find((v) => v.lang.toLowerCase() === "en-us") ?? enVoices[0];
  };

  // Speaks the current step starting from `fromCharIdx` (0 = full step).
  // Passing a saved absolute char position resumes from the exact word where
  // the user paused — no sentence repeat.
  // onend/onerror only reset state if this utterance is still the active one
  // — protects against races when cancel() interrupts to start a new one.
  const speakText = (fromCharIdx = 0) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const synth = window.speechSynthesis;
    synth.cancel();
    // Chrome keeps a sticky global "paused" flag: if the engine was paused
    // (user paused a previous step, then we start a fresh utterance), speak()
    // queues silently until resume(). Clear it so the new utterance plays.
    if (synth.paused) synth.resume();

    const allParts = buildStepParts();
    const fullText = allParts.join('. ');
    const text = fullText.substring(fromCharIdx);
    if (!text) return;

    resumeCharIdxRef.current = fromCharIdx;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === "tl" ? "tl-PH" : "en-US";
    const voice = pickVoice(language);
    if (voice) utterance.voice = voice;
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Absolute start positions of each part in the full text.
    const allPartStarts: number[] = [];
    let pos = 0;
    for (const part of allParts) {
      allPartStarts.push(pos);
      pos += part.length + 2; // ". " separator
    }

    // Positions relative to the resume text. Negative for parts that began
    // before fromCharIdx — intentional: the onboundary reverse-scan finds the
    // correct current sentence because the last entry with start ≤ charIndex
    // (ci ≥ 0) is still the right sentence even when that start is negative.
    const sentenceStarts = allPartStarts.map(start => start - fromCharIdx);

    // Sentence-scoped field highlighting.
    // onboundary (Edge/Safari) is primary: cancels setTimeout timers on first
    // fire and drives highlights precisely per word. Chrome often skips
    // onboundary entirely so the setTimeout fallback covers it.
    highlightTimersRef.current.forEach(clearTimeout);
    highlightTimersRef.current = [];
    const fieldHighlights = currentStep?.fieldHighlights;

    // setTimeout fallback for field highlights (cancelled when onboundary fires).
    if (fieldHighlights?.length) {
      // Neural voices speak at ~3–4 words/sec at rate=1.0; using 4.0 so timers
      // fire early enough to not be cancelled when TTS ends for longer steps.
      const WORDS_PER_MS = (4.0 * utterance.rate) / 1000;
      let timerMs = 0;

      for (let i = 0; i < allParts.length; i++) {
        const partStart = allPartStarts[i];
        const partEnd = partStart + allParts[i].length;
        if (partEnd <= fromCharIdx) continue; // entirely before resume point

        const partText = allParts[i];
        // Count only the words that fall within the resume text for this part.
        const textInResume = partStart >= fromCharIdx
          ? partText
          : partText.substring(fromCharIdx - partStart);
        const wordCount = Math.max(1, textInResume.trim().split(/\s+/).length);
        const durationMs = wordCount / WORDS_PER_MS;
        const startMs = timerMs;
        timerMs += durationMs;

        const matches = fieldHighlights.filter(fh =>
          partText.toLowerCase().includes(fh.phrase.toLowerCase())
        );
        const key = matches
          .map(fh => fh.id ? `id:${fh.id}` : fh.selector ? `sel:${fh.selector}` : '')
          .filter(Boolean)
          .join(',');

        if (key) {
          highlightTimersRef.current.push(
            setTimeout(() => setActiveHighlightKey(key), startMs)
          );
          const nextPart = allParts[i + 1];
          const nextHasHighlights = nextPart
            ? fieldHighlights.some(fh => nextPart.toLowerCase().includes(fh.phrase.toLowerCase()))
            : false;
          if (!nextHasHighlights) {
            highlightTimersRef.current.push(
              setTimeout(() => setActiveHighlightKey(''), timerMs)
            );
          }
        }
      }
    }

    let boundaryFired = false;
    utterance.onboundary = (event) => {
      const ci = event.charIndex; // relative to resume text
      // Save absolute position so a subsequent pause resumes from this word.
      resumeCharIdxRef.current = fromCharIdx + ci;

      let sentIdx = 0;
      for (let i = sentenceStarts.length - 1; i >= 0; i--) {
        if (sentenceStarts[i] <= ci) { sentIdx = i; break; }
      }

      if (fieldHighlights?.length) {
        if (!boundaryFired) {
          boundaryFired = true;
          highlightTimersRef.current.forEach(clearTimeout);
          highlightTimersRef.current = [];
        }
        const sentText = allParts[sentIdx] ?? '';
        const key = fieldHighlights
          .filter(fh => sentText.toLowerCase().includes(fh.phrase.toLowerCase()))
          .map(fh => fh.id ? `id:${fh.id}` : fh.selector ? `sel:${fh.selector}` : '')
          .filter(Boolean)
          .join(',');
        setActiveHighlightKey(key);
      }
    };

    const settleIfCurrent = () => {
      if (currentUtteranceRef.current === utterance) {
        highlightTimersRef.current.forEach(clearTimeout);
        highlightTimersRef.current = [];
        setIsSpeaking(false);
        setIsPaused(false);
        setActiveHighlightKey('');
        currentUtteranceRef.current = null;
      }
    };
    utterance.onend = settleIfCurrent;
    utterance.onerror = settleIfCurrent;
    currentUtteranceRef.current = utterance;
    synth.speak(utterance);
    setIsSpeaking(true);
    setIsPaused(false);
  };

  const handleSpeakToggle = (e: MouseEvent) => {
    e.stopPropagation();
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const synth = window.speechSynthesis;

    // Reading right now → pause. Auto-play stays armed but isPaused gates the
    // step-change continuation, so this doubles as "stop": the next step won't
    // read until the user resumes.
    if (isSpeaking && !isPaused) {
      // Null the ref BEFORE cancel() so the cancelled utterance's onend
      // (settleIfCurrent) no-ops and doesn't reset isSpeaking/isPaused.
      // We use cancel() instead of pause() because Chromium-based browsers
      // (Chrome/Edge) fire onend on pause(), making resume() restart from scratch.
      currentUtteranceRef.current = null;
      synth.cancel();
      setIsPaused(true);
      highlightTimersRef.current.forEach(clearTimeout);
      highlightTimersRef.current = [];
      setActiveHighlightKey('');
      return;
    }

    // Paused → resume. If the paused utterance is still alive, resume it in
    // place; if it already finished (or we paused across a step change), start
    // the current step fresh.
    if (isPaused) {
      setIsPaused(false);
      setAutoPlay(true);
      // Restart from the exact char position where the user paused
      // (resumeCharIdxRef is updated on every onboundary word event).
      speakText(resumeCharIdxRef.current);
      return;
    }

    // Idle or finished-but-armed → (re)start reading and arm auto-play so it
    // continues onto the next steps.
    setAutoPlay(true);
    speakText();
  };

  // Cancel any in-flight speech when the step or language changes, and on
  // unmount, so the synthesizer doesn't keep reading the previous step.
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Chrome populates the voice list asynchronously. Calling getVoices() here
  // primes the load; voiceschanged keeps the list warm so pickVoice() has
  // a populated list on the first TTS click.
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const prime = () => { window.speechSynthesis.getVoices(); };
    prime();
    window.speechSynthesis.addEventListener?.("voiceschanged", prime);
    return () => {
      window.speechSynthesis.removeEventListener?.("voiceschanged", prime);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    // Keep reading into the new step whenever auto-play is armed — even if the
    // previous step's audio already finished (that's the whole point: the
    // intent survives a natural utterance end). Paused users stay paused: we
    // cancel the audio but preserve isPaused so Next doesn't sneak past their
    // stop, and the speaker icon stays on "Resume". Language flips force a
    // fresh utterance so the voice/locale gets re-picked.
    const shouldContinue = autoPlay && !isPaused;
    if (!shouldContinue) {
      // Null the ref *before* cancel() so the cancelled utterance's onend
      // handler (settleIfCurrent) no-ops and doesn't clobber isPaused.
      currentUtteranceRef.current = null;
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    speakText(); // starts from part 0; returns early if step has no text
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, language]);

  // Clear field highlights and reset sentence tracking on every step change.
  // Prevents a saved part index from a previous step bleeding into the new one.
  useEffect(() => {
    setActiveHighlightKey('');
    resumeCharIdxRef.current = 0;
  }, [currentStep]);

  // Blink engine: whenever activeHighlightKey changes, find the target elements
  // and toggle an orange border on a 600ms interval. Cleans up on next change
  // or when the key is cleared (speech ends / pauses / step changes).
  //
  // Strategy: target the MuiOutlinedInput fieldset (the element that provides
  // the visible border in MUI outlined inputs). Changing its borderColor/Width
  // mirrors exactly what MUI does on focus/error, so it's always visible and
  // never clipped by parent overflow the way an `outline` can be.
  // Falls back to box-shadow on the wrapper if no fieldset is found.
  useEffect(() => {
    if (!activeHighlightKey) return;
    const wrappers: HTMLElement[] = [];
    const targets: HTMLElement[] = []; // fieldsets (preferred) or wrappers
    for (const token of activeHighlightKey.split(',')) {
      let found: HTMLElement | null = null;
      if (token.startsWith('id:')) {
        found = document.getElementById(token.slice(3));
      } else if (token.startsWith('sel:')) {
        found = document.querySelector<HTMLElement>(token.slice(4));
      }
      if (found) {
        // Walk up to the MUI input container regardless of whether the id is
        // on a visible <input>, a Select combobox div, or a hidden <input>.
        const wrapper = found.closest<HTMLElement>('.MuiInputBase-root') ?? found;
        wrappers.push(wrapper);
        // The fieldset inside OutlinedInput is the actual painted border —
        // animating it avoids any overflow-clipping issues with outline/shadow.
        const fieldset = wrapper.querySelector<HTMLElement>('fieldset');
        targets.push(fieldset ?? wrapper);
      }
    }
    if (targets.length === 0) return;
    wrappers[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const origBorderColor = targets.map(el => el.style.borderColor);
    const origBorderWidth = targets.map(el => el.style.borderWidth);
    // Also keep a box-shadow fallback on wrappers for non-fieldset targets.
    const origBoxShadow = wrappers.map(el => el.style.boxShadow);
    let on = true;
    const apply = () => {
      targets.forEach(el => {
        el.style.borderColor = on ? '#f97316' : '';
        el.style.borderWidth = on ? '2px' : '';
      });
      wrappers.forEach(el => {
        el.style.boxShadow = on ? '0 0 0 2px rgba(249,115,22,0.4)' : '';
      });
      on = !on;
    };
    apply();
    const timer = setInterval(apply, 600);
    return () => {
      clearInterval(timer);
      targets.forEach((el, i) => {
        el.style.borderColor = origBorderColor[i];
        el.style.borderWidth = origBorderWidth[i];
      });
      wrappers.forEach((el, i) => {
        el.style.boxShadow = origBoxShadow[i];
      });
    };
  }, [activeHighlightKey]);

  useEffect(() => {
    const handleEscapeBlock = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("keydown", handleEscapeBlock, true);

    return () => {
      document.removeEventListener("keydown", handleEscapeBlock, true);
    };
  }, []);

  // Arrow-key navigation (paged variant only). Left = previous, Right = next.
  // Ignored when focus is in a text input/textarea so typing isn't hijacked.
  useEffect(() => {
    if (!isPaged) return;
    const handleArrowKeys = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        target?.isContentEditable
      ) {
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        if (isLastStep) {
          // Mirror the Finish/Take Quiz button: route to onFinish if the
          // consumer wired a follow-up flow, otherwise end the walkthrough.
          if (onFinish) {
            onFinish();
          } else {
            onEnd();
          }
        } else {
          onNext();
        }
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        if (
          onPrevious &&
          typeof currentStepIndex === "number" &&
          currentStepIndex > 0
        ) {
          onPrevious();
        }
      }
    };
    document.addEventListener("keydown", handleArrowKeys);
    return () => document.removeEventListener("keydown", handleArrowKeys);
  }, [isPaged, isLastStep, onNext, onEnd, onFinish, onPrevious, currentStepIndex]);

  const computeRects = () => {
    if (!currentStep?.highlights) return [];
    return currentStep.highlights
      .map((item) => {
        // `selector` (querySelector) wins over `id` (getElementById) so steps can
        // target elements without a stable id, e.g. a third-party widget button.
        const el = item.selector
          ? document.querySelector(item.selector)
          : item.id
            ? document.getElementById(item.id)
            : null;
        return el ? el.getBoundingClientRect() : null;
      })
      .filter(Boolean) as DOMRect[];
  };

  useEffect(() => {
    const rects = computeRects();
    setHighlightRects(rects);

    // Scroll the first highlight into view if it's off-screen (above or below
    // the viewport). Common for tabs/sections that live further down the page
    // — without this the spotlight lands on an element the user can't see.
    // `block: "center"` keeps the element comfortably away from the top/bottom
    // edges so the caption card has room to render alongside it.
    if (rects.length > 0) {
      const first = rects[0];
      const vh = window.innerHeight;
      const isOffscreen = first.top < 0 || first.bottom > vh;
      if (isOffscreen && currentStep?.highlights?.[0]) {
        const item = currentStep.highlights[0];
        const el = item.selector
          ? document.querySelector<HTMLElement>(item.selector)
          : item.id
            ? document.getElementById(item.id)
            : null;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    // Auto-advance to next step if highlighted elements aren't visible.
    // Suppressed while isBackwardNavigating is true: a backward transition
    // intentionally closes the current step's modal/menu (removing its element)
    // before the new step index is set, so a missing element here is expected
    // and must not race-advance the tour forward.
    //
    // The timeout RE-CHECKS before advancing: on first open / auto-start a
    // step's target can mount a beat late (lazy content), so a missing element
    // on the first pass is not yet a reason to skip. The rAF retries below pick
    // it up within a frame; we only advance if it's *still* gone after a grace
    // window — otherwise a slow-loading target would be silently skipped.
    // Auto-advance past a step whose element isn't found — but only when the
    // step doesn't opt out. Steps that open a Portal/Dialog set skipAutoAdvance
    // so the walkthrough waits indefinitely for the element to appear.
    let autoAdvanceTimer: ReturnType<typeof setTimeout> | null = null;
    if (
      currentStep &&
      !currentStep.skipAutoAdvance &&
      rects.length === 0 &&
      !isBackwardNavigating?.current
    ) {
      autoAdvanceTimer = setTimeout(() => {
        if (computeRects().length === 0 && !isBackwardNavigating?.current) {
          onNext();
        }
      }, currentStep.fastSkip ? 80 : 800);
    }

    const onUpdate = () => setHighlightRects(computeRects());
    window.addEventListener("resize", onUpdate);
    window.addEventListener("scroll", onUpdate, true);

    // Recompute the anchor on the next frames too. On first open / auto-start
    // the page is often still settling (sidebar, lazy content), so the initial
    // anchor rect can be stale and the card lands off-screen until a resize.
    // These rAF passes re-read the anchor once layout settles — no zoom needed.
    const raf1 = requestAnimationFrame(onUpdate);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(onUpdate));

    // Multi-stage retries for steps that open a MUI Dialog (Portal + Slide
    // animation ≈225ms). The rAF passes fire before the Portal flushes on slow
    // machines; these timeouts cover the realistic range of render latency so
    // the spotlight appears without requiring a resize or user interaction.
    const retry200 = setTimeout(onUpdate, 200);
    const retry450 = setTimeout(onUpdate, 450);
    const retry700 = setTimeout(onUpdate, 700);

    return () => {
      if (autoAdvanceTimer !== null) clearTimeout(autoAdvanceTimer);
      clearTimeout(retry200);
      clearTimeout(retry450);
      clearTimeout(retry700);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", onUpdate);
      window.removeEventListener("scroll", onUpdate, true);
    };
  }, [currentStep, onNext, isBackwardNavigating]);

  // Measure the popover card after every render/content change so placement can
  // react to its real footprint. A ResizeObserver catches size changes from
  // language toggles, sub-caption expansion, font reflow, etc.
  useLayoutEffect(() => {
    const el = paperRef.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      setPaperSize((prev) =>
        prev && prev.w === rect.width && prev.h === rect.height
          ? prev
          : { w: rect.width, h: rect.height },
      );
    };

    measure();
    // Re-measure on the next frames: on first open the synchronous measurement
    // can run before Emotion styles / web fonts have fully laid the card out,
    // yielding a too-small height that never changes again (so the
    // ResizeObserver never re-fires — which is why only a manual zoom used to
    // fix it). The rAF passes re-measure once layout has settled.
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
    };
  }, [currentStep, language, isPaged, highlightRects]);

  if (!currentStep || highlightRects.length === 0) return null;

  const captionStyle = getCaptionStyle(
    highlightRects,
    currentStep.captionPosition,
    paperSize ?? undefined,
  );
  const clip = createClipPath(highlightRects);

  const handleBackdropClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isLastStep) {
      handleEnd();
    } else {
      if (numberOfClicks > 0) {
        setNumberOfClicks(numberOfClicks - 1);
        if (onScrollView) {
          onScrollView();
        }
      } else {
        if (currentStep?.clicksOfNext) {
          setNumberOfClicks(currentStep?.clicksOfNext);
        }
        onNext();
      }
    }
  };

  const handleSkipClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isLastStep) {
      onEnd();
    } else {
      setOpenDialog(true);
    }
    // window.location.reload();
  };

  const handleHoleClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (isLastStep) {
      handleEnd();
    } else {
      if (numberOfClicks > 0) {
        setNumberOfClicks(numberOfClicks - 1);
        if (onScrollView) {
          console.log("naclick ang scroll");
          onScrollView();
        }
      } else {
        if (currentStep?.clicksOfNext) {
          console.log("naclick to");
          setNumberOfClicks(currentStep?.clicksOfNext);
        }
        onNext();
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmDialog = () => {
    onEnd();
  };

  const handleEnd = () => {
    onEnd();
    //   window.location.reload();
  };

  // In paged mode the chrome controls advancement; clicking the backdrop or
  // the spotlighted hole should not move the tour forward.
  const backdropClickHandler = isPaged ? undefined : handleBackdropClick;
  const holeClickHandler = isPaged ? undefined : handleHoleClick;

  return (
    <Modal
      open
      onClose={handleEnd}
      disableEscapeKeyDown
      aria-labelledby="walkthrough-modal-title"
      hideBackdrop
      sx={{ zIndex: 1700 }}
      data-testid="walkthrough-modal"
    >
      <>
        {/* Single backdrop with multiple holes */}
        <Box
          data-testid="walkthrough-overlay"
          onClick={backdropClickHandler}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1800,
            clipPath: clip,
            WebkitClipPath: clip,
            transition: "clip-path 280ms cubic-bezier(0.4, 0, 0.2, 1)",
            cursor: isPaged ? "default" : "pointer",
          }}
        />

        {/* Floating "Click anywhere to continue" hint — classic variant only */}
        {!isPaged && (
          <Box
            sx={{
              position: "fixed",
              left: "50%",
              bottom: 32,
              transform: "translateX(-50%)",
              zIndex: 2200,
              pointerEvents: "none",
              color: "white",
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 500,
              letterSpacing: 0.5,
              fontSize: "1 rem",
              maxWidth: 360,
              textAlign: "center",
              whiteSpace: "pre-line",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <img
                src="/tap_icon.svg"
                alt="Tap_Logo"
                style={{
                  width: "50px",
                  height: "50px",
                  objectFit: "contain",
                }}
              />
              <Typography
                sx={{
                  fontSize: "inherit",
                  fontWeight: "bold",
                  color: "inherit",
                  textAlign: "center",
                  WebkitTextStroke: "1px rgba(2, 11, 41, 1)",
                }}
              >
                Click anywhere to{"\n"}continue
              </Typography>
            </Box>
          </Box>
        )}

        {/* Absorb clicks over the spotlighted holes. In classic mode this advances
            the tour; in paged mode it just blocks the underlying UI from firing. */}
        {highlightRects.map((rect, index) => (
          <Box
            key={index}
            onClick={holeClickHandler}
            onKeyDown={
              isPaged
                ? undefined
                : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onNext();
                  }
                }
            }
            role={isPaged ? undefined : "button"}
            tabIndex={isPaged ? -1 : 0}
            aria-label={isPaged ? undefined : "Highlight area"}
            sx={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              zIndex: 1900,
              borderRadius: "8px",
              pointerEvents: "auto",
              cursor: isPaged ? "default" : "pointer",
              transition:
                "top 280ms cubic-bezier(0.4, 0, 0.2, 1), left 280ms cubic-bezier(0.4, 0, 0.2, 1), width 280ms cubic-bezier(0.4, 0, 0.2, 1), height 280ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        ))}

        <Paper
          data-testid="walkthrough-card"
          sx={{
            position: "absolute" as const,
            ...captionStyle,
            backgroundColor: "white",
            boxShadow: isPaged
              ? "0 12px 32px -8px rgba(18, 50, 87, 0.25), 0 4px 12px -4px rgba(0, 0, 0, 0.08)"
              : 24,
            borderRadius: isPaged ? 2 : undefined,
            transition: isPaged
              ? "top 280ms cubic-bezier(0.4, 0, 0.2, 1), left 280ms cubic-bezier(0.4, 0, 0.2, 1), bottom 280ms cubic-bezier(0.4, 0, 0.2, 1), right 280ms cubic-bezier(0.4, 0, 0.2, 1)"
              : undefined,
            mt: currentStep?.isButtonOrSearchBar
              ? -0.5
              : currentStep?.adjustMarginTop,
            p: isPaged ? 2 : 1.5,
            pt: 0,
            pb: isPaged ? 2 : 1.5,
            px: isPaged ? 2.25 : 1.5,
            zIndex: 2000,
            minWidth: 280,
            maxWidth: CAPTION_W,
            // Never let the card exceed the viewport — on short tablet/laptop
            // screens it caps here and the body scrolls internally (below),
            // keeping the navigation footer on-screen and clickable.
            maxHeight: `calc(100vh - ${EDGE_PAD * 2}px)`,
            overflow: "hidden",
            cursor: isPaged ? "default" : "pointer",
            userSelect: "none",
            display: "flex",
            flexDirection: "column",
          }}
          ref={paperRef}
          onMouseDown={(e) => {
            // Prevent the native mousedown from reaching document so MUI's
            // ClickAwayListener doesn't close open menus/popovers when the
            // user clicks walkthrough buttons (Prev / Next / Close).
            e.nativeEvent.stopImmediatePropagation();
          }}
          onClick={
            isPaged
              ? (e) => e.stopPropagation()
              : (e) => {
                e.stopPropagation();
                if (isLastStep) {
                  handleEnd();
                } else {
                  onNext();
                }
              }
          }
          tabIndex={isPaged ? -1 : 0}
          role={isPaged ? undefined : "button"}
          aria-label={isPaged ? undefined : "Continue walkthrough"}
          onKeyDown={
            isPaged
              ? undefined
              : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (isLastStep) {
                    handleEnd();
                  } else {
                    onNext();
                  }
                }
              }
          }
        >
          {isPaged && (groupInfo || (typeof currentStepIndex === "number" && typeof totalSteps === "number")) && (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ pt: 2, pb: 0.75, flexWrap: "wrap", rowGap: 0.5 }}
            >
              {typeof currentStepIndex === "number" &&
                typeof totalSteps === "number" &&
                groupInfo && (
                  <Chip
                    size="small"
                    label={`STEP ${groupInfo.indexInGroup} OF ${groupInfo.groupSize}`}
                    sx={{
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      letterSpacing: 0.3,
                      height: 20,
                      borderRadius: 999,
                      px: 0.25,
                    }}
                  />
                )}
              {groupInfo && (
                <Chip
                  size="small"
                  label={resolveLocalized(groupInfo.name, language)}
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "secondary.contrastText",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    height: 20,
                    borderRadius: 999,
                    px: 0.25,
                  }}
                />
              )}
              <Box display='flex' gap={1} marginLeft="auto !important">
                {/* Read-aloud toggle — hidden on Chrome only when Tagalog (robotic TTS). */}
                {!(isChrome && language === "tl") && (
                  <Tooltip
                      title={
                        isPaused
                          ? "Resume reading"
                          : isSpeaking
                            ? "Pause reading"
                            : "Read aloud"
                      }
                      arrow
                      placement="top"
                      slotProps={{ popper: { style: { zIndex: 9999 } } }}
                    >
                      <IconButton
                        size="small"
                        onClick={handleSpeakToggle}
                        sx={{
                          ml: "auto !important",
                          color: "primary.main",
                          p: 0.25,
                          "&:hover": { bgcolor: "rgba(18,50,87,0.06)" },
                        }}
                        aria-label={
                          isPaused
                            ? "Resume reading walkthrough aloud"
                            : isSpeaking
                              ? "Pause reading walkthrough aloud"
                              : "Read walkthrough aloud"
                        }
                      >
                        {isPaused ? (
                          <PlayArrow sx={{ fontSize: 18 }} />
                        ) : isSpeaking ? (
                          <Pause sx={{ fontSize: 18 }} />
                        ) : (
                          <VolumeUp sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                )}
                {/* Minimal language toggle — single Translate icon. Tooltip
                  shows the language it will switch TO so the action is clear. */}
                <Tooltip
                  title={language === "en" ? "Switch to Tagalog" : "Switch to English"}
                  arrow
                  placement="top"
                  slotProps={{ popper: { style: { zIndex: 9999 } } }}
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      setLanguageAndPersist(language === "en" ? "tl" : "en")
                    }
                    sx={{
                      color: "primary.main",
                      p: 0.25,
                      "&:hover": { bgcolor: "rgba(18,50,87,0.06)" },
                    }}
                    aria-label="Toggle walkthrough language"
                  >
                    <Translate sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          )}
          {/* Scrollable body: caps to the space left between the pinned header
              (chips/controls) and footer (progress + nav buttons). When the
              content is taller than the viewport allows, this region scrolls
              instead of pushing the footer off-screen. */}
          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f0f0f0",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#bdbdbd",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#9e9e9e",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                pt: isPaged ? 1.25 : 0,
              }}
            >
            <Typography
              id="walkthrough-modal-title"
              variant="body2"
              fontWeight={isPaged ? 700 : 600}
              color="primary.main"
              sx={
                isPaged
                  ? {
                    fontSize: "1rem",
                    lineHeight: 1.3,
                    letterSpacing: "-0.01em",
                  }
                  : undefined
              }
              paddingTop={
                !isPaged &&
                  typeof currentStep?.title === "string" &&
                  currentStep.title.length >= 25
                  ? 1.5
                  : 0
              }
            >
              {renderL(currentStep.title)}
            </Typography>
            {!isPaged && (
              <IconButton
                onClick={handleSkipClick}
                sx={{ color: "secondary.main" }}
              >
                <Close />
              </IconButton>
            )}
          </Box>
          {(() => {
            // Suppress the tip card entirely when there's no caption text —
            // some steps lean on `subCaption` only (e.g. Registration
            // Masterlist) and would otherwise render an empty box.
            // Resolve to the active language first so an empty Localized
            // caption ({ en: "", tl: "" }) is treated as no caption.
            const resolvedCaption = resolveLocalized(currentStep.caption, language);
            const hasCaption =
              typeof resolvedCaption === "string"
                ? resolvedCaption.trim().length > 0
                : resolvedCaption != null;
            if (!hasCaption) return null;
            return isPaged ? (
              // "Tip card" look — soft navy tint with a left accent stripe.
              // Bolded terms inside still render in primary.main, so the
              // accent visually ties to the in-line emphasis.
              <Box
                sx={{
                  mt: 1.25,
                  py: 1.25,
                  px: 1.5,
                  borderLeft: "4px solid",
                  borderColor: "primary.main",
                  bgcolor: "rgba(18,50,87,0.05)",
                  borderRadius: "0 6px 6px 0",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.primary",
                    whiteSpace: "pre-line",
                    lineHeight: 1.55,
                    textAlign: "justify",
                    fontSize: '0.8rem'
                  }}
                >
                  {renderL(currentStep.caption)}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "text.secondary",
                  whiteSpace: "pre-line",
                  textAlign: "justify",
                  fontSize: '0.8rem'
                }}
              >
                {renderL(currentStep.caption)}
              </Typography>
            );
          })()}
          {Array.isArray(currentStep.subCaption) &&
            currentStep.subCaption.length > 0 && (
              <Box
                sx={{
                  mt: isPaged ? 1.5 : 1,
                  overflowY: currentStep.showScrollbar ? "auto" : "visible",
                  maxHeight: currentStep.showScrollbar ? "180px" : "none",
                  // Same tip-card treatment as the main caption (paged only)
                  // so the scrollable section reads as part of the same
                  // emphasis block instead of floating bullets.
                  ...(isPaged && {
                    py: 1.25,
                    pl: 1.5,
                    pr: currentStep.showScrollbar ? 1 : 1.5,
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                    bgcolor: "rgba(18,50,87,0.05)",
                    borderRadius: "0 6px 6px 0",
                  }),
                  ...(!isPaged && {
                    pr: currentStep.showScrollbar ? 0.5 : 0,
                  }),
                  /* Scrollbar styles */
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f0f0f0",
                    borderRadius: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#bdbdbd",
                    borderRadius: "8px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#9e9e9e",
                  },
                }}
              >
                {currentStep.subCaption.map((section, index) => (
                  <Box key={index} sx={{ mt: index === 0 ? 0 : isPaged ? 1.5 : 1 }}>
                    {/* Section Title */}
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "text.secondary", fontSize: '0.83rem' }}
                    >
                      {renderL(section.title)}
                    </Typography>

                    {/* Subtitles as bullets */}
                    {Array.isArray(section.subTitle) &&
                      section.subTitle.map((sub, subIndex) => (
                        <React.Fragment key={subIndex}>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", ml: 2, fontSize: '0.8rem' }}
                          >
                            • {renderL(sub.subTitle)}
                          </Typography>

                          {Array.isArray(sub.miniTitle) &&
                            sub.miniTitle.map((mini, minIndex) => (
                              <React.Fragment key={minIndex}>
                                {/* Mini Title */}
                                <Typography
                                  variant="body2"
                                  sx={{ color: "text.secondary", ml: 4, fontSize: '0.8rem' }}
                                >
                                  • {renderL(mini.miniTitle)}
                                </Typography>

                                {/* SUPER MINI TITLE*/}
                                {Array.isArray(mini.superMiniTitle) &&
                                  mini.superMiniTitle.map((superMini, smIndex) => (
                                    <Typography
                                      key={smIndex}
                                      variant="body2"
                                      sx={{ color: "text.secondary", ml: 6, fontSize: '0.8rem' }}
                                    >
                                      • {renderL(superMini.superMiniTitle)}
                                    </Typography>
                                  ))}
                              </React.Fragment>
                            ))}
                        </React.Fragment>
                      ))}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {isPaged &&
            typeof currentStepIndex === "number" &&
            typeof totalSteps === "number" &&
            totalSteps > 0 && (
              <Box sx={{ mt: 2.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                  }}
                >
                  {onGoToStep ? (
                    <Slider
                      value={sliderDraft}
                      min={0}
                      max={(totalSteps ?? 1) - 1}
                      step={1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value + 1}`}
                      onChange={(_, value) => setSliderDraft(value as number)}
                      onChangeCommitted={(_, value) => onGoToStep(value as number)}
                      sx={{
                        flex: 1,
                        color: "secondary.main",
                        height: 4,
                        padding: "13px 0",
                        "& .MuiSlider-thumb": {
                          width: 12,
                          height: 12,
                          transition: "none",
                          "&:hover, &.Mui-focusVisible": {
                            boxShadow: "0 0 0 6px rgba(255,107,53,0.16)",
                          },
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "grey.200",
                          opacity: 1,
                        },
                        "& .MuiSlider-track": {
                          border: "none",
                        },
                        "& .MuiSlider-valueLabel": {
                          fontSize: 11,
                          fontWeight: 700,
                          py: 0.25,
                          px: 0.75,
                          borderRadius: 1,
                          backgroundColor: "secondary.main",
                        },
                      }}
                    />
                  ) : (
                    <LinearProgress
                      variant="determinate"
                      value={((currentStepIndex ?? 0) + 1) / (totalSteps ?? 1) * 100}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 999,
                        backgroundColor: "grey.200",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          backgroundColor: "secondary.main",
                        },
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {currentStepIndex + 1} / {totalSteps}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    mt: 1.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    data-testid="walkthrough-btn-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSkipClick(e);
                    }}
                    sx={{
                      textTransform: "none",
                      minWidth: 0,
                      color: "text.secondary",
                      px: 1.25,
                      borderRadius: 999,
                      transition:
                        "background-color 180ms ease, color 180ms ease",
                      "&:hover": { backgroundColor: "grey.100" },
                    }}
                  >
                    Close
                  </Button>
                  <Box sx={{ display: "flex", gap: 0.75 }}>
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      data-testid="walkthrough-btn-prev"
                      startIcon={<ArrowBackIosNew sx={{ fontSize: '16px !important' }} />}
                      disabled={currentStepIndex <= 0 || !onPrevious}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPrevious?.();
                      }}
                      sx={{
                        textTransform: "none",
                        minWidth: 0,
                        color: "text.secondary",
                        px: 1.25,
                        borderRadius: 999,
                        transition:
                          "background-color 180ms ease, color 180ms ease",
                        "&:hover": { backgroundColor: "grey.100" },
                      }}
                    >
                      Prev
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disableElevation
                      data-testid="walkthrough-btn-next"
                      endIcon={!isLastStep && <ArrowForwardIos sx={{ fontSize: '14px !important' }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isLastStep) {
                          // Route to onFinish if the consumer provided one
                          // (e.g. post-walkthrough quiz flow); otherwise the
                          // classic "Finish == end" semantics apply.
                          if (onFinish) {
                            onFinish();
                          } else {
                            handleEnd();
                          }
                        } else {
                          onNext();
                        }
                      }}
                      sx={{
                        textTransform: "none",
                        minWidth: 0,
                        borderRadius: 999,
                        px: 1.75,
                        fontWeight: 600,
                      }}
                    >
                      {isLastStep ? finishLabel : "Next"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
        </Paper>
        {onDismiss ? (
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            maxWidth={"xs" as any}
            PaperProps={{ sx: { borderRadius: 2, width: "90vw", maxWidth: 560 } }}
            sx={{ zIndex: 2500 }}
          >
            <DialogTitle sx={{ fontWeight: 700, color: "primary.main", pb: 1 }}>
              Stop the walkthrough?
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: "text.secondary" }}>
                You can pick up where you left off anytime by clicking the ℹ info icon.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseDialog}
                variant="text"
                color="inherit"
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  color: "text.secondary",
                  px: 2,
                }}
              >
                Continue
              </Button>
              <Button
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseDialog();
                  setTimeout(() => onDismiss(), 0);
                }}
                variant="outlined"
                color="primary"
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2,
                  fontWeight: 600,
                }}
              >
                Skip for now
              </Button>
              <Button
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseDialog();
                  setTimeout(() => onEnd(), 0);
                }}
                variant="contained"
                color="primary"
                disableElevation
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 2,
                  fontWeight: 600,
                }}
              >
                Don't show again
              </Button>
            </DialogActions>
          </Dialog>
        ) : (
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            maxWidth={"xs" as any}
            fullWidth
            sx={{ zIndex: 2500 }}
          >
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to stop the system walkthrough?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="inherit" sx={{ textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDialog}
                variant="contained"
                autoFocus
                sx={{ textTransform: "none" }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    </Modal>
  );
};

export default Walkthrough;
